import React, { useState, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";

export default function ResetPassword() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const token = useMemo(() => (sp.get("token") || "").trim(), [sp]);
  const [pw, setPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error("Missing reset token — request a new link.");
      return;
    }
    if (pw.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    if (pw !== confirmPw) {
      toast.error("Passwords don't match");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      toast.success(data?.message || "Password updated.");
      setTimeout(() => nav("/login"), 1800);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not reset password");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center px-6">
        <div data-testid="reset-no-token" className="max-w-md text-center bg-black/70 backdrop-blur-xl rounded-lg p-10 border border-white/5">
          <h1 className="text-2xl font-bold mb-3">Reset link is missing</h1>
          <p className="text-sm text-gray-400 mb-6">
            This page expects a reset token in the URL. Request a new link from the forgot-password page.
          </p>
          <Link to="/forgot-password" className="btn-red inline-block px-6 py-2 rounded font-semibold">
            Request a new link
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white relative">
      <img
        src="https://images.unsplash.com/photo-1759842952767-456f1b6dded8?crop=entropy&cs=srgb&fm=jpg&q=85&w=2400"
        className="absolute inset-0 w-full h-full object-cover opacity-40"
        alt=""
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/80 via-[#0A0A0A]/85 to-[#0A0A0A]" />
      <header className="relative z-10 px-6 lg:px-12 py-6">
        <Link to="/" className="text-[#E50914] font-black text-2xl">CINECINEMA</Link>
      </header>
      <main className="relative z-10 max-w-md mx-auto px-6 py-12">
        <div data-testid="reset-password-card" className="bg-black/70 backdrop-blur-xl rounded-lg p-10 border border-white/5 shadow-2xl">
          {!done ? (
            <>
              <h1 className="text-3xl font-bold mb-2">Set a new password</h1>
              <p className="text-sm text-gray-400 mb-6">
                Pick something at least 8 characters long. You&apos;ll be signed in with the new password right after.
              </p>
              <form onSubmit={submit} className="space-y-4">
                <input
                  data-testid="reset-password-new"
                  type="password"
                  required
                  minLength={8}
                  placeholder="New password (≥8 chars)"
                  className="input-dark"
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                />
                <input
                  data-testid="reset-password-confirm"
                  type="password"
                  required
                  minLength={8}
                  placeholder="Confirm new password"
                  className="input-dark"
                  value={confirmPw}
                  onChange={(e) => setConfirmPw(e.target.value)}
                />
                <button
                  data-testid="reset-password-submit"
                  type="submit"
                  disabled={loading}
                  className="btn-red w-full py-3 rounded font-semibold"
                >
                  {loading ? "Updating..." : "Reset password"}
                </button>
              </form>
            </>
          ) : (
            <div data-testid="reset-password-done" className="text-center">
              <div className="w-14 h-14 bg-[#22c55e]/10 border border-[#22c55e]/40 rounded-full flex items-center justify-center mx-auto mb-5">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold mb-2">Password updated</h2>
              <p className="text-sm text-gray-400">Redirecting you to sign in…</p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
